import { useEffect, useId, useState, type FormEvent } from 'react';
import { accountDisplayName, accountNumberDigits } from '../api/accounts';
import { errorMessage } from '../api/client';
import type {
  CreateDdAccountRequest,
  CreateDdAccountResponse,
  DdAccount,
  DdAccountKind,
  DdAccountKindSpec,
  ListDdAccountsResponse,
  UpdateDdAccountRequest,
} from '../api/types';
import { Button } from './button';
import { ConfirmDialog } from './confirm-dialog';
import { Input } from './input';
import { Label } from './label';

/** How long a revealed full number stays on screen before it hides itself. */
const REVEAL_MS = 30_000;

export interface AccountRosterActions {
  create: (body: CreateDdAccountRequest) => Promise<CreateDdAccountResponse>;
  update: (accountId: string, body: UpdateDdAccountRequest) => Promise<DdAccount>;
  /** Admin only — omit it and no merge control is offered. */
  merge?: (accountId: string, intoAccountId: string) => Promise<DdAccount>;
  /** The FULL number (audited server-side). Called only on an explicit click. */
  reveal: (accountId: string) => Promise<string>;
}

/**
 * The DD ACCOUNT ROSTER (ADR-0037): the room's real accounts, grouped by kind,
 * with add / edit, an explicit "Show full number" (when one is on file), and —
 * for the admin — merge. Each account becomes its own checklist category, so this
 * list is how the checklist's "one category per account" items get answered.
 * Only the last 4 digits are ever shown, except a number the person asked to see.
 * Presentational: the app passes the loaded roster and its API calls.
 */
export function AccountRoster({
  data,
  kinds,
  actions,
}: {
  data: ListDdAccountsResponse;
  /** Show only these kinds (a roster question / one placeholder item); default all. */
  kinds?: DdAccountKind[];
  actions: AccountRosterActions;
}) {
  const shown = data.kinds.filter((k) => !kinds || kinds.includes(k.kind));
  if (shown.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        There are no account items on this checklist to list accounts for.
      </p>
    );
  }
  return (
    <div className="space-y-4">
      {shown.map((spec) => (
        <KindSection
          key={spec.kind}
          spec={spec}
          showHeading={shown.length > 1}
          fullNumbersEnabled={data.fullNumbersEnabled}
          accounts={data.accounts.filter((a) => a.kind === spec.kind)}
          actions={actions}
        />
      ))}
    </div>
  );
}

function KindSection({
  spec,
  showHeading,
  fullNumbersEnabled,
  accounts,
  actions,
}: {
  spec: DdAccountKindSpec;
  showHeading: boolean;
  fullNumbersEnabled: boolean;
  accounts: DdAccount[];
  actions: AccountRosterActions;
}) {
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  return (
    <section className="space-y-2" aria-label={spec.plural}>
      {showHeading ? <h4 className="text-sm font-semibold">{spec.plural}</h4> : null}
      {accounts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No {spec.plural.toLowerCase()} listed yet.
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {accounts.map((a) => (
            <AccountRow
              key={a.id}
              account={a}
              spec={spec}
              fullNumbersEnabled={fullNumbersEnabled}
              others={accounts.filter((o) => o.id !== a.id)}
              actions={actions}
            />
          ))}
        </ul>
      )}
      {adding ? (
        <AccountForm
          spec={spec}
          fullNumbersEnabled={fullNumbersEnabled}
          submitLabel={`Add ${spec.noun}`}
          onCancel={() => setAdding(false)}
          onSubmit={async (fields) => {
            const res = await actions.create({ kind: spec.kind, ...createBody(fields) });
            setAdding(false);
            setNotice(
              res.created
                ? `Added ${accountDisplayName(res.account)}.`
                : `${accountDisplayName(res.account)} was already on the list.`,
            );
          }}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setNotice(null);
              setAdding(true);
            }}
          >
            Add {spec.noun}
          </Button>
          {notice ? (
            <span role="status" className="text-xs text-muted-foreground">
              {notice}
            </span>
          ) : null}
        </div>
      )}
    </section>
  );
}

function AccountRow({
  account,
  spec,
  fullNumbersEnabled,
  others,
  actions,
}: {
  account: DdAccount;
  spec: DdAccountKindSpec;
  fullNumbersEnabled: boolean;
  others: DdAccount[];
  actions: AccountRosterActions;
}) {
  const [editing, setEditing] = useState(false);
  const [merging, setMerging] = useState(false);
  const name = accountDisplayName(account);

  return (
    <li className="space-y-2 px-3 py-2.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="break-words text-sm font-medium">{name}</p>
          <p className="text-xs text-ink-3">
            {account.source === 'ai' ? 'Found by the AI in a document' : 'Added by hand'}
          </p>
        </div>
        {!editing && !merging ? (
          <div className="flex flex-wrap items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
            {actions.merge && others.length > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => setMerging(true)}>
                Merge…
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {account.hasFullNumber ? (
        <RevealNumber account={account} reveal={actions.reveal} />
      ) : null}
      {editing ? (
        <AccountForm
          spec={spec}
          fullNumbersEnabled={fullNumbersEnabled}
          account={account}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={async (fields) => {
            await actions.update(account.id, updateBody(fields, account));
            setEditing(false);
          }}
        />
      ) : null}
      {merging && actions.merge ? (
        <MergeControl
          account={account}
          others={others}
          merge={actions.merge}
          onDone={() => setMerging(false)}
        />
      ) : null}
    </li>
  );
}

/** "Show full number" → the number (until Hide, or 30 seconds). Never fetched
 * on its own; the value lives only in this row's state while it is shown. */
function RevealNumber({
  account,
  reveal,
}: {
  account: DdAccount;
  reveal: (accountId: string) => Promise<string>;
}) {
  const [number, setNumber] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (number === null) return;
    const t = setTimeout(() => setNumber(null), REVEAL_MS);
    return () => clearTimeout(t);
  }, [number]);

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {number !== null ? (
        <>
          <span className="font-mono text-sm" aria-label={`Full number of ${account.label}`}>
            {number}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setNumber(null)}>
            Hide
          </Button>
        </>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          disabled={pending}
          onClick={async () => {
            setPending(true);
            setError(null);
            try {
              setNumber(await reveal(account.id));
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setPending(false);
            }
          }}
        >
          {pending ? 'Showing…' : 'Show full number'}
        </Button>
      )}
      {error ? (
        <span role="alert" className="text-destructive">
          {error}
        </span>
      ) : null}
    </div>
  );
}

function MergeControl({
  account,
  others,
  merge,
  onDone,
}: {
  account: DdAccount;
  others: DdAccount[];
  merge: (accountId: string, intoAccountId: string) => Promise<DdAccount>;
  onDone: () => void;
}) {
  const selectId = useId();
  const [into, setInto] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const target = others.find((o) => o.id === into) ?? null;
  const from = accountDisplayName(account);

  return (
    <div className="space-y-1.5 rounded-md bg-surface-2 p-2.5">
      <label htmlFor={selectId} className="block text-xs font-medium">
        Merge {from} into another account (the same account listed twice)
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <select
          id={selectId}
          value={into}
          onChange={(e) => setInto(e.target.value)}
          className="min-w-0 flex-1 rounded border border-input bg-background px-2 py-1 text-sm"
        >
          <option value="">Choose the account to keep…</option>
          {others.map((o) => (
            <option key={o.id} value={o.id}>
              {accountDisplayName(o)}
            </option>
          ))}
        </select>
        <Button size="sm" disabled={!target} onClick={() => setConfirming(true)}>
          Merge
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {confirming && target ? (
        <ConfirmDialog
          danger
          title={`Merge ${from} into ${accountDisplayName(target)}?`}
          confirmLabel="Merge accounts"
          pending={pending}
          onCancel={() => setConfirming(false)}
          onConfirm={async () => {
            setPending(true);
            setError(null);
            try {
              await merge(account.id, target.id);
              setConfirming(false);
              onDone();
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setPending(false);
            }
          }}
        >
          <p>
            {from} is removed from the list. Its documents move to{' '}
            {accountDisplayName(target)}&apos;s categories, its own categories are retired and its
            checklist items are removed.
          </p>
          <p>This can&apos;t be undone.</p>
          {error ? (
            <p role="alert" className="text-destructive">
              {error}
            </p>
          ) : null}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

interface AccountFields {
  institution: string;
  nickname: string;
  /** 'full' = the whole account number; 'last4' = only its last 4 digits. */
  mode: 'full' | 'last4';
  accountNumber: string;
  last4: string;
}

function orNull(v: string): string | null {
  const t = v.trim();
  return t ? t : null;
}

/** The POST body: only the fields given; a full number OR the last 4, never both. */
function createBody(f: AccountFields): Omit<CreateDdAccountRequest, 'kind'> {
  const body: Omit<CreateDdAccountRequest, 'kind'> = {};
  const institution = orNull(f.institution);
  const nickname = orNull(f.nickname);
  if (institution) body.institution = institution;
  if (nickname) body.nickname = nickname;
  if (f.mode === 'full') {
    const n = orNull(f.accountNumber);
    if (n) body.accountNumber = n;
  } else {
    const l4 = orNull(f.last4);
    if (l4) body.last4 = l4;
  }
  return body;
}

/** The PATCH body. A blank full-number field keeps the stored number; switching
 * to "last 4 only" clears a stored number. */
function updateBody(f: AccountFields, account: DdAccount): UpdateDdAccountRequest {
  const body: UpdateDdAccountRequest = {
    institution: orNull(f.institution),
    nickname: orNull(f.nickname),
  };
  if (f.mode === 'full') {
    // Blank keeps what is on file (the stored number, or the last 4).
    const n = orNull(f.accountNumber);
    if (n) body.accountNumber = n;
  } else {
    body.last4 = orNull(f.last4);
    if (account.hasFullNumber) body.accountNumber = null;
  }
  return body;
}

/** A client-side check mirroring the API's rules, so the person learns at once. */
function validate(f: AccountFields, editingWithStoredNumber: boolean): string | null {
  if (f.mode === 'full' && f.accountNumber.trim()) {
    const digits = accountNumberDigits(f.accountNumber.trim());
    if (!/^\d{5,34}$/.test(digits)) return 'An account number is 5 to 34 digits.';
  }
  if (f.mode === 'last4' && f.last4.trim() && !/^\d{4}$/.test(f.last4.trim())) {
    return 'The last 4 digits are exactly 4 digits.';
  }
  const hasNumber =
    (f.mode === 'full' && (f.accountNumber.trim() !== '' || editingWithStoredNumber)) ||
    (f.mode === 'last4' && f.last4.trim() !== '');
  if (!f.institution.trim() && !f.nickname.trim() && !hasNumber) {
    return 'Give the account an institution, a name or the last 4 digits of its number.';
  }
  return null;
}

function AccountForm({
  spec,
  fullNumbersEnabled,
  account,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  spec: DdAccountKindSpec;
  fullNumbersEnabled: boolean;
  /** Editing this account; absent when adding. */
  account?: DdAccount;
  submitLabel: string;
  onSubmit: (fields: AccountFields) => Promise<void>;
  onCancel: () => void;
}) {
  const id = useId();
  const fullAllowed = fullNumbersEnabled && spec.fullNumberAllowed;
  const [fields, setFields] = useState<AccountFields>({
    institution: account?.institution ?? '',
    nickname: account?.nickname ?? '',
    mode: fullAllowed && (!account || account.hasFullNumber) ? 'full' : 'last4',
    accountNumber: '',
    last4: account?.last4 ?? '',
  });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<AccountFields>) => setFields((f) => ({ ...f, ...patch }));
  const keepsStored = fields.mode === 'full' && account?.hasFullNumber === true;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problem = validate(fields, keepsStored);
    if (problem) {
      setError(problem);
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onSubmit(fields);
    } catch (err) {
      setError(errorMessage(err));
      setPending(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="space-y-3 rounded-md border border-dashed p-3"
      aria-label={account ? `Edit ${account.label}` : `Add ${spec.noun}`}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={`${id}-inst`}>{spec.institutionNoun}</Label>
          <Input
            id={`${id}-inst`}
            value={fields.institution}
            maxLength={80}
            disabled={pending}
            onChange={(e) => set({ institution: e.target.value })}
            placeholder={spec.kind === 'bank_account' ? 'e.g. Chase' : undefined}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${id}-nick`}>Friendly name</Label>
          <Input
            id={`${id}-nick`}
            value={fields.nickname}
            maxLength={80}
            disabled={pending}
            onChange={(e) => set({ nickname: e.target.value })}
            placeholder={spec.kind === 'credit_card' ? 'e.g. Frank’s AMEX' : 'e.g. Operating'}
          />
        </div>
      </div>

      {fields.mode === 'full' ? (
        <div className="space-y-1">
          <Label htmlFor={`${id}-num`}>Account number</Label>
          <Input
            id={`${id}-num`}
            value={fields.accountNumber}
            inputMode="numeric"
            autoComplete="off"
            maxLength={64}
            disabled={pending}
            onChange={(e) => set({ accountNumber: e.target.value })}
            placeholder={
              account?.hasFullNumber && account.last4
                ? `Number on file ending ${account.last4} — leave blank to keep it`
                : undefined
            }
          />
          <p className="text-xs text-muted-foreground">
            Stored encrypted. Only the last 4 digits are shown.{' '}
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              disabled={pending}
              onClick={() => set({ mode: 'last4', accountNumber: '' })}
            >
              {account?.hasFullNumber
                ? 'Keep only the last 4 digits instead'
                : 'I only have the last 4 digits'}
            </button>
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          <Label htmlFor={`${id}-last4`}>Last 4 digits</Label>
          <Input
            id={`${id}-last4`}
            value={fields.last4}
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            disabled={pending}
            onChange={(e) => set({ last4: e.target.value })}
            className="max-w-[8rem]"
          />
          {fullAllowed ? (
            <p className="text-xs text-muted-foreground">
              <button
                type="button"
                className="font-medium text-primary hover:underline"
                disabled={pending}
                onClick={() => set({ mode: 'full' })}
              >
                Enter the full account number instead
              </button>
            </p>
          ) : spec.fullNumberAllowed ? null : (
            <p className="text-xs text-muted-foreground">
              Only the last 4 digits of a {spec.noun} are kept.
            </p>
          )}
          {account?.hasFullNumber ? (
            <p className="text-xs text-muted-foreground">
              Saving removes the full number on file.
            </p>
          ) : null}
        </div>
      )}

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
